import React, { useEffect, useRef } from 'react';
import { MENTION_RE, splitBody, kindOf, idOf } from './mentions';

const MAX_LEN = 4000;
const isChip = (n) => n && n.nodeType === 1 && n.classList && n.classList.contains('cm-chip');
const isTrailBr = (n) => n && n.nodeType === 1 && n.nodeName === 'BR' && n.hasAttribute('data-trail');

/** DOM -> body string (text, chips as [@u{id}|Name] / [@c{id}|Name], <br>/blocks as \n). */
export function serialize(root) {
    let out = '';
    const walk = (node) => {
        node.childNodes.forEach(n => {
            if (n.nodeType === 3) out += n.data.replace(/​/g, '');
            else if (isChip(n)) out += `[@${n.getAttribute('data-kind') || 'u'}${n.getAttribute('data-uid')}|${(n.getAttribute('data-name') || '').replace(/[[\]]/g, '')}]`;
            else if (n.nodeName === 'BR') { if (!isTrailBr(n)) out += '\n'; }
            else if (n.nodeName === 'DIV' || n.nodeName === 'P') {
                if (out && !out.endsWith('\n')) out += '\n';
                walk(n);
            } else walk(n);
        });
    };
    walk(root);
    return out;
}

function makeChip(kind, id, name) {
    const c = document.createElement('span');
    c.className = kind === 'c' ? 'cm-chip cm-chip--contact' : 'cm-chip'; c.contentEditable = 'false';
    c.setAttribute('data-kind', kind); c.setAttribute('data-uid', String(id)); c.setAttribute('data-name', name);
    c.textContent = `@${name}`;
    return c;
}

/** Append text (newlines -> <br>) to a parent/fragment. */
function appendText(parent, text) {
    text.split('\n').forEach((line, i) => {
        if (i > 0) parent.appendChild(document.createElement('br'));
        if (line) parent.appendChild(document.createTextNode(line));
    });
}

function setCaret(node, offset) {
    const sel = window.getSelection(); if (!sel) return;
    const r = document.createRange(); r.setStart(node, offset); r.collapse(true);
    sel.removeAllRanges(); sel.addRange(r);
}

export default function MentionEditor({ value, onChange, onQueryChange, onKeyDown, placeholder, maxLength = MAX_LEN, editorRef }) {
    const root = useRef(null);
    const lastFromDom = useRef(null);
    const queryInfo = useRef(null);

    // A trailing <br> renders no line, so keep one invisible spare after it.
    const normalizeTail = () => {
        const el = root.current; if (!el) return;
        [...el.querySelectorAll('br[data-trail]')].forEach(b => b.remove());
        el.normalize();
        const last = el.lastChild;
        if (last && last.nodeName === 'BR') { const t = document.createElement('br'); t.setAttribute('data-trail', '1'); el.appendChild(t); }
    };

    const emit = (afterDelete) => {
        const el = root.current; if (!el) return;
        let s = serialize(el);
        if ((!s || (afterDelete && !s.trim())) && el.childNodes.length) { el.innerHTML = ''; s = ''; }
        normalizeTail();
        lastFromDom.current = s;
        onChange && onChange(s);
    };

    const reportQuery = () => {
        const sel = window.getSelection();
        let info = null;
        if (sel && sel.rangeCount && sel.isCollapsed && root.current && root.current.contains(sel.anchorNode) && sel.anchorNode.nodeType === 3) {
            const node = sel.anchorNode; const off = sel.anchorOffset;
            const m = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(node.data.slice(0, off));
            if (m) info = { query: m[2], node, start: off - m[2].length - 1, end: off };
        }
        queryInfo.current = info;
        onQueryChange && onQueryChange(info ? { query: info.query } : null);
    };

    const renderBody = (body) => {
        const el = root.current; el.innerHTML = '';
        splitBody(body).forEach(p => {
            if (p.type === 'text') appendText(el, p.text);
            else { el.appendChild(makeChip(p.kind, p.id, p.name)); }
        });
        normalizeTail();
    };

    // Controlled only for external sets (initial body, clear after post).
    useEffect(() => {
        if (value !== lastFromDom.current) { renderBody(value || ''); lastFromDom.current = value || ''; }
    }, [value]); // eslint-disable-line

    const insertAtCaret = (text) => {
        const el = root.current; const sel = window.getSelection();
        if (!sel.rangeCount || !el.contains(sel.anchorNode)) { el.focus(); const r = document.createRange(); r.selectNodeContents(el); r.collapse(false); sel.removeAllRanges(); sel.addRange(r); }
        const range = sel.getRangeAt(0); range.deleteContents();
        const frag = document.createDocumentFragment(); appendText(frag, text);
        const lastNode = frag.lastChild; if (!lastNode) return;
        range.insertNode(frag);
        const r2 = document.createRange(); r2.setStartAfter(lastNode); r2.collapse(true);
        sel.removeAllRanges(); sel.addRange(r2);
    };

    if (editorRef) {
        editorRef.current = {
            focus: () => { const el = root.current; if (!el) return; el.scrollIntoView({ block: 'nearest' }); el.focus(); },
            clear: () => { if (root.current) { root.current.innerHTML = ''; lastFromDom.current = ''; } },
            appendMention: (user) => {
                const el = root.current; if (!el) return;
                const before = serialize(el);
                if (before.trim() && !/\s$/.test(before)) el.appendChild(document.createTextNode(' '));
                el.appendChild(makeChip(kindOf(user), idOf(user), user.name));
                const sp = document.createTextNode(' '); el.appendChild(sp);
                el.focus(); setCaret(sp, 1); emit(); reportQuery();
            },
            insertMention: (user) => {
                const info = queryInfo.current; if (!info || !info.node.parentNode) return;
                const { node, start, end } = info; const data = node.data;
                const parent = node.parentNode;
                const beforeNode = document.createTextNode(data.slice(0, start));
                const chip = makeChip(kindOf(user), idOf(user), user.name);
                const sp = document.createTextNode(' ' + data.slice(end));
                parent.insertBefore(beforeNode, node); parent.insertBefore(chip, node); parent.insertBefore(sp, node);
                parent.removeChild(node);
                if (!beforeNode.data) parent.removeChild(beforeNode);
                root.current.focus(); setCaret(sp, 1); emit(); reportQuery();
            },
        };
    }

    const removeChipAt = (chip) => {
        const parent = chip.parentNode; const idx = [...parent.childNodes].indexOf(chip);
        const sib = chip.nextSibling;
        if (sib && sib.nodeType === 3 && sib.data.startsWith(' ')) { sib.deleteData(0, 1); if (!sib.data) sib.remove(); }
        chip.remove();
        const r = document.createRange(); r.setStart(parent, Math.min(idx, parent.childNodes.length)); r.collapse(true);
        const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
        emit(); reportQuery();
    };

    // The chip immediately before / after a collapsed caret, if any.
    const adjacentChip = (dir) => {
        const sel = window.getSelection();
        if (!sel.rangeCount || !sel.isCollapsed) return null;
        const n = sel.anchorNode; const o = sel.anchorOffset;
        if (n.nodeType === 3) {
            if (dir < 0) {
                if (o === 0 && isChip(n.previousSibling)) return n.previousSibling;
                if (o === 1 && n.data[0] === ' ' && isChip(n.previousSibling)) return n.previousSibling;
            } else if (o === n.data.length && isChip(n.nextSibling)) return n.nextSibling;
            return null;
        }
        const kids = n.childNodes;
        const c = dir < 0 ? kids[o - 1] : kids[o];
        return isChip(c) ? c : null;
    };

    const handleKeyDown = (e) => {
        onKeyDown && onKeyDown(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Backspace' || e.key === 'Delete') {
            const chip = adjacentChip(e.key === 'Backspace' ? -1 : 1);
            if (chip) { e.preventDefault(); removeChipAt(chip); }
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (serialize(root.current).length >= maxLength) return;
            insertAtCaret('\n'); emit(); reportQuery();
        }
    };

    const handleBeforeInput = (e) => {
        if (e.inputType && e.inputType.startsWith('insert') && serialize(root.current).length >= maxLength) e.preventDefault();
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = (e.clipboardData.getData('text/plain') || '').replace(/\r\n?/g, '\n');
        const room = maxLength - serialize(root.current).length;
        if (room <= 0) return;
        insertAtCaret(text.slice(0, room)); emit(); reportQuery();
    };

    return (
        <div ref={root} className="cm-editor" role="textbox" aria-multiline="true" contentEditable suppressContentEditableWarning
            data-placeholder={placeholder}
            onInput={(e) => { emit(e.nativeEvent && e.nativeEvent.inputType && e.nativeEvent.inputType.startsWith('delete')); reportQuery(); }} onKeyUp={reportQuery} onClick={reportQuery}
            onKeyDown={handleKeyDown} onBeforeInput={handleBeforeInput} onPaste={handlePaste} />
    );
}
